import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatAxiosError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Landmark, Check, Loader2, Copy } from "lucide-react";

/**
 * Mandats de prélèvement SEPA GoCardless — patron identique à la carte Stripe/PayPal. Paramètres de la société active,
 * ou (admin plateforme, `adminSlug`) d'une société choisie par le super-admin : mêmes champs, routes /admin.
 */
export default function GoCardlessSettings({ activeCompany, brand, fetchCompanies, adminSlug }) {
  const slug = adminSlug || activeCompany;
  const saveUrl = adminSlug ? `/admin/companies/${adminSlug}/gocardless` : `/companies/${activeCompany}/settings`;
  const testUrl = adminSlug ? `/admin/companies/${adminSlug}/gocardless-test` : `/companies/${activeCompany}/gocardless-test`;
  const [form, setForm] = useState({ gocardless_access_token: "", gocardless_webhook_secret: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);

  useEffect(() => {
    setForm({ gocardless_access_token: "", gocardless_webhook_secret: "" });
    setTest(null);
  }, [slug]);

  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhook/gocardless?company=${slug}`;
  const configured = !!brand.gocardless_token_set;

  const save = async () => {
    const payload = {};
    if (form.gocardless_access_token) payload.gocardless_access_token = form.gocardless_access_token;
    if (form.gocardless_webhook_secret) payload.gocardless_webhook_secret = form.gocardless_webhook_secret;
    if (!Object.keys(payload).length) return;
    setSaving(true);
    try {
      await api.put(saveUrl, payload);
      await fetchCompanies();
      setForm({ gocardless_access_token: "", gocardless_webhook_secret: "" });
      toast.success("Configuration GoCardless enregistrée");
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    setTest(null);
    try {
      const { data } = await api.post(testUrl);
      setTest(data);
      if (data.ok) toast.success(`Connexion GoCardless OK (${data.environment})`);
      else toast.error("Échec de la connexion GoCardless");
    } catch (e) {
      setTest({ ok: false, error: formatAxiosError(e) });
      toast.error("Échec du test GoCardless");
    } finally { setTesting(false); }
  };

  const copyWebhookUrl = () => { navigator.clipboard.writeText(webhookUrl); toast.success("URL copiée"); };

  return (
    <div className={adminSlug ? "border border-border rounded-sm p-4 space-y-4" : "bg-card border border-border rounded-sm p-6 lg:p-8 space-y-5 mt-8"} data-testid="gocardless-settings">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Landmark className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-semibold">GoCardless (mandats SEPA) · {brand.name}</h3>
        </div>
        <span className={`text-[10px] tracking-wide uppercase px-2 py-1 rounded-sm font-semibold ${configured ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-muted text-muted-foreground"}`} data-testid="gocardless-status">
          {configured ? `Configuré · ${brand.gocardless_environment === "live" ? "Live" : "Sandbox"}` : "Non configuré"}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">Connectez GoCardless pour collecter le mandat de prélèvement SEPA à la fin du dossier client.</p>

      <div>
        <Label>Jeton d'accès GoCardless</Label>
        <Input type="password" value={form.gocardless_access_token} data-testid="gocardless-token"
          onChange={(e) => setForm((f) => ({ ...f, gocardless_access_token: e.target.value }))}
          placeholder={configured ? "•••••••• (inchangé)" : "live_… ou sandbox_…"} className="mt-1.5 rounded-sm" />
        <p className="text-xs text-muted-foreground mt-1.5">Stocké de façon sécurisée, jamais renvoyé. Laissez vide pour conserver le jeton actuel.</p>
      </div>

      <div>
        <Label>Secret de webhook</Label>
        <Input type="password" value={form.gocardless_webhook_secret} data-testid="gocardless-webhook-secret"
          onChange={(e) => setForm((f) => ({ ...f, gocardless_webhook_secret: e.target.value }))}
          placeholder={brand.gocardless_webhook_secret_set ? "•••••••• (inchangé)" : "Secret de l'endpoint webhook"} className="mt-1.5 rounded-sm" />
        <p className="text-xs text-muted-foreground mt-1.5">Stocké de façon sécurisée, jamais renvoyé. Laissez vide pour conserver le secret actuel.</p>
      </div>

      <div>
        <Label>URL de webhook à renseigner chez GoCardless</Label>
        <div className="flex items-center gap-2 mt-1.5">
          <Input readOnly value={webhookUrl} data-testid="gocardless-webhook-url" className="rounded-sm font-mono text-xs bg-muted/40" />
          <Button type="button" variant="outline" size="icon" onClick={copyWebhookUrl} className="rounded-sm shrink-0" title="Copier l'URL">
            <Copy className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="pt-1 flex items-center gap-3 flex-wrap">
        <Button onClick={save} disabled={saving} data-testid="save-gocardless" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
          {saving ? "Enregistrement…" : "Enregistrer GoCardless"}
        </Button>
        <Button onClick={testConnection} disabled={testing || !configured} variant="outline" data-testid="test-gocardless" className="rounded-sm font-semibold">
          {testing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Test en cours…</> : "Tester la connexion"}
        </Button>
      </div>
      {test && (
        <div data-testid="gocardless-test-result" className={`text-sm rounded-sm px-3 py-2.5 border ${test.ok ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-800 border-red-200"}`}>
          {test.ok ? (
            <span><Check className="w-4 h-4 inline mr-1.5 -mt-0.5" />Connexion réussie — environnement <strong>{test.environment}</strong>{test.creditor_name ? <> · {test.creditor_name}</> : null}</span>
          ) : (
            <span><strong>Échec :</strong> {test.error}</span>
          )}
        </div>
      )}
    </div>
  );
}
